import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import purchaseRequestService from '../../services/purchaseRequestService';
import vendorService from '../../services/vendorService';

const emptyItem = () => ({ description: '', qty: 1, unit: '', est_unit_cost: '', est_total: 0 });

const priorities = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'emergency', label: 'Emergency (requires waiver justification)' },
];

/**
 * Procurement Request creation form - matches 01_Procurement_Request_Template.
 * Multi-item, budget-coded, picks a real Vendor + bank account instead of
 * free text. Submits through the SAME purchaseRequestService.createRequest
 * as the plain Purchase Request form, with request_type='procurement' - the
 * backend routes it through the identical approval pipeline.
 */
const ProcurementRequestForm = ({ onBack }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [requesterName, setRequesterName] = useState(user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : (user?.email || ''));
  const [department, setDepartment] = useState('');
  const [costCenterBudgetCode, setCostCenterBudgetCode] = useState('');
  const [requiredByDate, setRequiredByDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [justification, setJustification] = useState('');

  const [items, setItems] = useState([emptyItem()]);

  const [budgetAvailable, setBudgetAvailable] = useState('');
  const [glAccountCode, setGlAccountCode] = useState('');
  const [approvedBudgetAmount, setApprovedBudgetAmount] = useState('');
  const [currency, setCurrency] = useState('NGN');

  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [supplierId, setSupplierId] = useState('');
  const [supplierAccountId, setSupplierAccountId] = useState('');

  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('transfer');

  // Emergency justification (only when priority === 'emergency')
  const [emergencyNature, setEmergencyNature] = useState('');
  const [riskImpactOfDelay, setRiskImpactOfDelay] = useState('');
  const [whyStandardProcessNotFollowed, setWhyStandardProcessNotFollowed] = useState('');
  const [basisOfVendorSelection, setBasisOfVendorSelection] = useState('');
  const [comparativeQuoteObtained, setComparativeQuoteObtained] = useState('');
  const [comparativeQuoteDetails, setComparativeQuoteDetails] = useState('');
  const [departmentHeadApprovalDate, setDepartmentHeadApprovalDate] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await vendorService.listVendors();
        setVendors(res.data.data?.vendors || []);
      } catch (error) {
        console.error('Load vendors error:', error);
        toast.error('Failed to load vendors');
      } finally {
        setLoadingVendors(false);
      }
    })();
    (async () => {
      try {
        const res = await purchaseRequestService.getManagers();
        const dbManagers = res.data.data?.managers || [];
        setManagers(dbManagers.map((m) => ({
          value: m.id.toString(),
          label: `${m.name} - ${m.department_name || m.department}`,
        })));
      } catch (error) {
        console.error('Load managers error:', error);
        toast.error('Failed to load managers');
      }
    })();
  }, []);

  const selectedVendor = vendors.find((v) => v.id === parseInt(supplierId));
  const vendorAccounts = selectedVendor?.Accounts || [];

  useEffect(() => {
    // Auto-select the account when there's exactly one; clear when vendor changes.
    if (vendorAccounts.length === 1) {
      setSupplierAccountId(vendorAccounts[0].id.toString());
    } else {
      setSupplierAccountId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      const qty = parseFloat(next[index].qty) || 0;
      const unitCost = parseFloat(next[index].est_unit_cost) || 0;
      next[index].est_total = Math.round(qty * unitCost * 100) / 100;
      return next;
    });
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  const grandTotal = items.reduce((sum, it) => sum + (parseFloat(it.est_total) || 0), 0);

  const validate = () => {
    const newErrors = {};
    if (!requesterName.trim()) newErrors.requesterName = 'Requester name is required';
    if (!department.trim()) newErrors.department = 'Department/Subsidiary is required';
    if (!selectedManager) newErrors.selectedManager = 'Approving manager must be selected';
    if (!supplierId) newErrors.supplierId = 'Vendor must be selected';
    if (vendorAccounts.length > 1 && !supplierAccountId) newErrors.supplierAccountId = 'This vendor has more than one account - please select which one to use';
    if (items.length === 0 || items.every((it) => !it.description.trim())) newErrors.items = 'At least one item is required';
    if (grandTotal <= 0) newErrors.items = 'Total amount must be greater than zero';
    if (priority === 'emergency') {
      if (!emergencyNature.trim()) newErrors.emergencyNature = 'Required for Emergency priority';
      if (!riskImpactOfDelay.trim()) newErrors.riskImpactOfDelay = 'Required for Emergency priority';
      if (!whyStandardProcessNotFollowed.trim()) newErrors.whyStandardProcessNotFollowed = 'Required for Emergency priority';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('request_type', 'procurement');
      submitData.append('requester_name', requesterName.trim());
      submitData.append('department', department.trim());
      submitData.append('approving_manager_id', selectedManager);
      submitData.append('payment_method', paymentMethod);
      submitData.append('priority', priority);
      submitData.append('cost_center_budget_code', costCenterBudgetCode);
      submitData.append('required_by_date', requiredByDate);
      submitData.append('delivery_location', deliveryLocation);
      submitData.append('justification', justification);
      submitData.append('items', JSON.stringify(items.filter((it) => it.description.trim())));
      submitData.append('budget_available', budgetAvailable === 'yes');
      submitData.append('gl_account_code', glAccountCode);
      submitData.append('approved_budget_amount', approvedBudgetAmount);
      submitData.append('currency', currency);
      submitData.append('supplier_id', supplierId);
      if (supplierAccountId) submitData.append('supplier_account_id', supplierAccountId);

      if (priority === 'emergency') {
        submitData.append('emergency_nature', emergencyNature);
        submitData.append('risk_impact_of_delay', riskImpactOfDelay);
        submitData.append('why_standard_process_not_followed', whyStandardProcessNotFollowed);
        submitData.append('basis_of_vendor_selection', basisOfVendorSelection);
        submitData.append('comparative_quote_obtained', comparativeQuoteObtained === 'yes');
        submitData.append('comparative_quote_details', comparativeQuoteDetails);
        submitData.append('department_head_approval_date', departmentHeadApprovalDate);
      }

      await purchaseRequestService.createRequest(submitData);
      toast.success('Procurement request submitted successfully!');
      navigate('/purchase-requests/create');
    } catch (error) {
      console.error('Create procurement request error:', error);
      toast.error(error.response?.data?.message || 'Failed to create procurement request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6 flex items-center justify-between">
            <div>
              <button type="button" onClick={onBack} className="flex items-center gap-1 text-indigo-100 hover:text-white text-sm mb-2">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <h1 className="text-3xl font-bold text-white">🛒 Create Procurement Request</h1>
              <p className="text-indigo-100 mt-1">Multi-item request with budget coding and vendor selection</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Request Information */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Request Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Requested By *</label>
                <input type="text" value={requesterName} onChange={(e) => setRequesterName(e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-xl ${errors.requesterName ? 'border-red-300' : 'border-gray-200'}`} />
                {errors.requesterName && <p className="text-red-500 text-sm mt-1">{errors.requesterName}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Department / Subsidiary *</label>
                <input type="text" value={department} onChange={(e) => setDepartment(e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-xl ${errors.department ? 'border-red-300' : 'border-gray-200'}`} />
                {errors.department && <p className="text-red-500 text-sm mt-1">{errors.department}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Cost Center / Budget Code</label>
                <input type="text" value={costCenterBudgetCode} onChange={(e) => setCostCenterBudgetCode(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Required By Date</label>
                <input type="date" value={requiredByDate} onChange={(e) => setRequiredByDate(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl">
                  {priorities.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Location</label>
                <input type="text" value={deliveryLocation} onChange={(e) => setDeliveryLocation(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Approving Manager *</label>
                <select value={selectedManager} onChange={(e) => setSelectedManager(e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-xl ${errors.selectedManager ? 'border-red-300' : 'border-gray-200'}`}>
                  <option value="">Select manager</option>
                  {managers.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                {errors.selectedManager && <p className="text-red-500 text-sm mt-1">{errors.selectedManager}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl">
                  <option value="transfer">Transfer</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
            </div>
            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Justification / Business Need</label>
              <textarea value={justification} onChange={(e) => setJustification(e.target.value)} rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
            </div>
          </div>

          {/* Vendor Selection */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Vendor</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor *</label>
                <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} disabled={loadingVendors}
                  className={`w-full px-4 py-3 border-2 rounded-xl ${errors.supplierId ? 'border-red-300' : 'border-gray-200'}`}>
                  <option value="">{loadingVendors ? 'Loading vendors...' : 'Select vendor'}</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.company_name}</option>)}
                </select>
                {errors.supplierId && <p className="text-red-500 text-sm mt-1">{errors.supplierId}</p>}
              </div>
              {vendorAccounts.length > 1 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor Account *</label>
                  <select value={supplierAccountId} onChange={(e) => setSupplierAccountId(e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl ${errors.supplierAccountId ? 'border-red-300' : 'border-gray-200'}`}>
                    <option value="">This vendor has {vendorAccounts.length} accounts - select one</option>
                    {vendorAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.bank_name} - {a.account_name} ({a.account_number}){a.is_primary ? ' · Primary' : ''}
                      </option>
                    ))}
                  </select>
                  {errors.supplierAccountId && <p className="text-red-500 text-sm mt-1">{errors.supplierAccountId}</p>}
                </div>
              )}
              {vendorAccounts.length === 1 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor Account</label>
                  <p className="px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-xl text-sm text-gray-600">
                    {vendorAccounts[0].bank_name} - {vendorAccounts[0].account_name} ({vendorAccounts[0].account_number})
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Items Requested</h2>
              <button type="button" onClick={addItem} className="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700">
                <Plus className="h-4 w-4" /> Add Item
              </button>
            </div>
            {errors.items && <p className="text-red-500 text-sm mb-3">{errors.items}</p>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                    <th className="pb-2 pr-2">Description</th>
                    <th className="pb-2 px-2 w-20">Qty</th>
                    <th className="pb-2 px-2 w-24">Unit</th>
                    <th className="pb-2 px-2 w-32">Est. Unit Cost</th>
                    <th className="pb-2 px-2 w-32">Est. Total</th>
                    <th className="pb-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="py-2 pr-2">
                        <input type="text" value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded-lg" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="number" min="0" value={it.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded-lg" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="text" value={it.unit} onChange={(e) => updateItem(i, 'unit', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded-lg" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="number" min="0" value={it.est_unit_cost} onChange={(e) => updateItem(i, 'est_unit_cost', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded-lg" />
                      </td>
                      <td className="py-2 px-2 text-gray-700 font-medium">{Number(it.est_total || 0).toLocaleString()}</td>
                      <td className="py-2">
                        {items.length > 1 && (
                          <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
              <p className="text-lg font-bold text-gray-800">Grand Total: {currency} {grandTotal.toLocaleString()}</p>
            </div>
          </div>

          {/* Budget & Account Coding */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Budget & Account Coding</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Budget Available?</label>
                <select value={budgetAvailable} onChange={(e) => setBudgetAvailable(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl">
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">GL / Account Code</label>
                <input type="text" value={glAccountCode} onChange={(e) => setGlAccountCode(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Approved Budget Amount</label>
                <input type="number" min="0" value={approvedBudgetAmount} onChange={(e) => setApprovedBudgetAmount(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Currency</label>
                <input type="text" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
              </div>
            </div>
          </div>

          {/* Emergency Justification */}
          {priority === 'emergency' && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl shadow-lg p-8">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <h2 className="text-xl font-bold text-amber-900">Emergency Justification</h2>
              </div>
              <p className="text-sm text-amber-700 mb-6">
                Required when the standard competitive quotation / RFQ process is bypassed due to genuine urgent business need. This creates an Emergency Procurement Waiver record for later approval and retrospective review.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nature of the emergency *</label>
                  <textarea value={emergencyNature} onChange={(e) => setEmergencyNature(e.target.value)} rows={2}
                    className={`w-full px-4 py-3 border-2 rounded-xl ${errors.emergencyNature ? 'border-red-300' : 'border-gray-200'}`} />
                  {errors.emergencyNature && <p className="text-red-500 text-sm mt-1">{errors.emergencyNature}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Risk / impact of delay if the standard process is followed *</label>
                  <textarea value={riskImpactOfDelay} onChange={(e) => setRiskImpactOfDelay(e.target.value)} rows={2}
                    className={`w-full px-4 py-3 border-2 rounded-xl ${errors.riskImpactOfDelay ? 'border-red-300' : 'border-gray-200'}`} />
                  {errors.riskImpactOfDelay && <p className="text-red-500 text-sm mt-1">{errors.riskImpactOfDelay}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Why the standard multi-quote / RFQ process could not be followed *</label>
                  <textarea value={whyStandardProcessNotFollowed} onChange={(e) => setWhyStandardProcessNotFollowed(e.target.value)} rows={2}
                    className={`w-full px-4 py-3 border-2 rounded-xl ${errors.whyStandardProcessNotFollowed ? 'border-red-300' : 'border-gray-200'}`} />
                  {errors.whyStandardProcessNotFollowed && <p className="text-red-500 text-sm mt-1">{errors.whyStandardProcessNotFollowed}</p>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Basis of vendor selection</label>
                    <input type="text" placeholder="e.g. only known supplier, fastest lead time" value={basisOfVendorSelection} onChange={(e) => setBasisOfVendorSelection(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Department Head Approval Date</label>
                    <input type="date" value={departmentHeadApprovalDate} onChange={(e) => setDepartmentHeadApprovalDate(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">At least one comparative quote obtained?</label>
                    <select value={comparativeQuoteObtained} onChange={(e) => setComparativeQuoteObtained(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl">
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Comparative quote details / confirmation none were obtainable</label>
                    <input type="text" value={comparativeQuoteDetails} onChange={(e) => setComparativeQuoteDetails(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onBack} className="px-6 py-3 border-2 border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50">
              {loading ? 'Submitting...' : 'Submit Procurement Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProcurementRequestForm;
